/**
 * Learn More Tabs Component
 *
 * Pill-tab navigation between hardcoded spec-row panels (BUG 4).
 *
 * Both product-type panel sets (`art_print` and `canvas`) are SSR'd inline by
 * `snippets/learn-more-tabs.liquid` so there is no lazy-load step. One panel
 * set is `[hidden]` on initial render based on the active variant's
 * `art_product` metafield. We toggle visibility on:
 *   1. `ThemeEvents.variantUpdate` — true variant change via Section Rendering
 *      API morph (URL navigation, Shopify variant:update). The whole element
 *      is replaced so we just re-bind tab listeners.
 *   2. `sku-picker:variant-changed` — in-row style-picker switch. The section
 *      is NOT re-fetched, so we toggle `[hidden]` between panel-sets and
 *      re-scope tab/panel refs to the now-active set. Detail.productType is
 *      provided by sku-picker / style-picker.
 */

import { ThemeEvents } from '@theme/events';

class LearnMoreTabs extends HTMLElement {
  constructor() {
    super();
    this.currentTabIndex = 0;
  }

  connectedCallback() {
    this._bindActivePanelSet();

    // Listen for Section Rendering API variant updates (URL nav, Shopify
    // variant:update — these still cause a section morph).
    if (!this._variantHandler) {
      this._variantHandler = this.handleVariantUpdate.bind(this);
      document.addEventListener(ThemeEvents.variantUpdate, this._variantHandler);
    }

    // Listen for in-row style-picker variant switches (no section morph).
    if (!this._skuPickerHandler) {
      this._skuPickerHandler = this.handleSkuPickerVariantChanged.bind(this);
      document.addEventListener('sku-picker:variant-changed', this._skuPickerHandler);
    }
  }

  disconnectedCallback() {
    if (this._variantHandler) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._variantHandler);
      this._variantHandler = null;
    }
    if (this._skuPickerHandler) {
      document.removeEventListener('sku-picker:variant-changed', this._skuPickerHandler);
      this._skuPickerHandler = null;
    }
  }

  /**
   * Re-scope `this.tabs` / `this.panels` to the currently visible panel-set
   * and (re-)bind click handlers. Safe to call multiple times — listeners are
   * always attached to fresh DOM after replaceChildren / innerHTML swaps.
   */
  _bindActivePanelSet() {
    const activeSet = this.querySelector('.learn-more__panel-set:not([hidden])');
    const scope = activeSet || this;

    this.tabs = scope.querySelectorAll('.learn-more__tab');
    this.panels = scope.querySelectorAll('.learn-more__panel');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = parseInt(tab.dataset.tabIndex, 10);
        this.switchTab(index);
      });
    });
  }

  /**
   * Switches to a specific tab.
   * @param {number} index - The tab index to switch to.
   */
  switchTab(index) {
    if (index === this.currentTabIndex) return;
    if (index < 0 || index >= this.tabs.length) return;

    this.currentTabIndex = index;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('learn-more__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('learn-more__panel--active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  /**
   * Reset the active panel-set's tabs to the first tab. Used when toggling
   * between art_print and canvas — the tab counts differ (3 vs 2) and the
   * activated tab may be stale for the new set.
   */
  _resetTabsToFirst() {
    this.currentTabIndex = 0;
    this.tabs.forEach((tab, i) => {
      const isActive = i === 0;
      tab.classList.toggle('learn-more__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.panels.forEach((panel, i) => {
      const isActive = i === 0;
      panel.classList.toggle('learn-more__panel--active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  /**
   * In-row style-picker switch. The section is NOT re-fetched, so we just
   * toggle which panel-set is visible and re-bind tab handlers.
   * @param {CustomEvent} event
   */
  handleSkuPickerVariantChanged(event) {
    const productType = event?.detail?.productType;
    if (productType !== 'canvas' && productType !== 'art_print') return;

    const sets = this.querySelectorAll('.learn-more__panel-set');
    if (!sets.length) return;

    let didChange = false;
    sets.forEach((set) => {
      const wasHidden = set.hasAttribute('hidden');
      if (set.dataset.productType === productType) {
        if (wasHidden) {
          set.removeAttribute('hidden');
          didChange = true;
        }
      } else if (!wasHidden) {
        set.setAttribute('hidden', '');
        didChange = true;
      }
    });

    if (didChange) {
      // Re-scope refs to the now-active panel-set and rebind click handlers.
      this._bindActivePanelSet();
      this._resetTabsToFirst();
    }
  }

  /**
   * Re-render when the active variant changes via the Section Rendering API
   * morph path (URL navigation, Shopify variant:update). Both product-type
   * panel sets are present in the new HTML — the SSR'd `[hidden]` attributes
   * already reflect the active variant.
   * @param {CustomEvent} event - The variant update event.
   */
  handleVariantUpdate(event) {
    const html = event?.detail?.data?.html;
    if (!html) return;

    const newLearnMore = html.querySelector('learn-more-tabs');
    if (!newLearnMore) {
      this.style.display = 'none';
      return;
    }

    const hasContent = newLearnMore.querySelector('.learn-more');
    if (hasContent) {
      this.innerHTML = newLearnMore.innerHTML;
      this.style.display = '';

      // Re-bind to fresh DOM — listeners attach to the now-visible panel-set.
      // Listener registration on document is guarded so we don't double-bind.
      this.currentTabIndex = 0;
      this._bindActivePanelSet();
    } else {
      this.style.display = 'none';
    }
  }
}

if (!customElements.get('learn-more-tabs')) {
  customElements.define('learn-more-tabs', LearnMoreTabs);
}
